import type {
  FileMetadata,
  FileStorageAdapter,
  FileStorageConfig
} from "@/types/FileStorageInterfaces.ts";
import {IndexedDBAdapter} from "@/adapters/IndexedDBFileStorageAdapter.ts";
import {ServerAdapter} from "@/adapters/ServerFileStorageAdapter.ts";
import {HybridAdapter} from "@/adapters/HybridFileStorageAdapter.ts";

export class FileStorageService {
  private adapter: FileStorageAdapter;

  constructor(config: FileStorageConfig) {
    this.adapter = this.createAdapter(config);
  }

  private createAdapter(config: FileStorageConfig): FileStorageAdapter {
    switch (config.adapter) {
      case 'indexeddb':
        return new IndexedDBAdapter();
      case 'server':
        if (!config.serverEndpoint) {
          throw new Error('Server endpoint required for server adapter');
        }
        return new ServerAdapter(config.serverEndpoint);
      case 'hybrid':
        if (!config.serverEndpoint) {
          throw new Error('Server endpoint required for server adapter');
        }
        return new HybridAdapter(config.serverEndpoint);
      default:
        throw new Error(`Unknown adapter type: ${config.adapter}`);
    }
  }

  async storeFile(file: File | Uint8Array, metadata: Omit<FileMetadata, 'id'>): Promise<string> {
    return await this.adapter.store(file, metadata);
  }

  async retrieveFile(id: string): Promise<Blob> {
    return await this.adapter.retrieve(id);
  }

  async deleteFile(id: string): Promise<boolean> {
    return await this.adapter.delete(id);
  }

  async getFileUrl(id: string): Promise<string> {
    return await this.adapter.getUrl(id);
  }

  async fileExists(id: string): Promise<boolean> {
    return await this.adapter.exists(id);
  }

  // Convenience methods for common operations
  async storeThumbnail(file: File, eventId: number): Promise<string> {
    return await this.storeFile(file, {
      filename: `thumbnail_${Date.now()}.${file.name.split('.').pop()}`,
      mimeType: file.type,
      size: file.size,
      eventId,
      fileType: 'THUMBNAIL'
    });
  }

  async storeImage(file: File, eventId: number): Promise<string> {
    return await this.storeFile(file, {
      filename: `image_${Date.now()}.${file.name.split('.').pop()}`,
      mimeType: file.type,
      size: file.size,
      eventId,
      fileType: 'IMAGE'
    });
  }
}
